/**
 * TopNav Component - Top navigation bar with logo, search, notifications, team switcher, and user menu
 * @module components/starnexus/common/TopNav
 */

import React from 'react';
import TopNavigation from '@amzn/awsui-components-console/top-navigation';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/starnexus/authStore';
import { useUIStore } from '../../../stores/starnexus/uiStore';
import amazonLogo from '../../../assets/Amazon_Logo_White_RGB.png';

// ============================================================================
// TopNav Component
// ============================================================================

export const TopNav: React.FC = () => {
  const navigate = useNavigate();
  const alias = useAuthStore((state) => state.alias);
  const role = useAuthStore((state) => state.role);
  const activeTeam = useAuthStore((state) => state.activeTeam);
  const theme = useUIStore((state) => state.theme);
  const toggleTheme = useUIStore((state) => state.toggleTheme);

  const handleUtilityClick = React.useCallback(
    (event: CustomEvent<{ id: string }>) => {
      const { id } = event.detail;

      switch (id) {
        case 'signout':
          navigate('/');
          break;
        case 'profile':
          navigate('/starnexus/settings');
          break;
        case 'theme':
          toggleTheme();
          break;
        default:
          break;
      }
    },
    [navigate, toggleTheme]
  );

  const utilities = React.useMemo(
    () => [
      // Theme toggle
      {
        type: 'button' as const,
        iconName: theme === 'dark' ? ('status-positive' as const) : ('status-pending' as const),
        title: theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode',
        ariaLabel: theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode',
        onClick: () => toggleTheme()
      },
      // Notifications
      {
        type: 'button' as const,
        iconName: 'notification' as const,
        title: 'Notifications',
        ariaLabel: 'Notifications (0)',
        badge: false,
        disableUtilityCollapse: false
      },
      // User menu
      {
        type: 'menu-dropdown' as const,
        text: alias || 'User',
        description: activeTeam ? `Team: ${activeTeam.teamName}` : '',
        iconName: 'user-profile' as const,
        onItemClick: handleUtilityClick,
        items: [
          { id: 'profile', text: 'Profile & Settings' },
          {
            id: 'role',
            text: `Role: ${role || 'Unknown'}`,
            disabled: true
          },
          { id: 'theme', text: theme === 'dark' ? 'Light Mode' : 'Dark Mode' },
          { id: 'signout', text: 'Sign out' }
        ]
      }
    ],
    [alias, activeTeam, role, theme, toggleTheme, handleUtilityClick]
  );

  return (
    <div id="top-nav" style={{ position: 'sticky', top: 0, zIndex: 1002 }}>
      <TopNavigation
        identity={{
          href: '/starnexus',
          title: 'IVT STARCATCHER',
          logo: { src: amazonLogo, alt: 'StarNexus Platform' }
        }}
        utilities={utilities}
        i18nStrings={{
          searchIconAriaLabel: 'Search',
          searchDismissIconAriaLabel: 'Close search',
          overflowMenuTriggerText: 'More',
          overflowMenuTitleText: 'All'
        }}
      />
    </div>
  );
};

export default TopNav;
